/**
 * Renderiza duas listas de recomendações lado a lado: "Recomendados" (pesos
 * primary) e "Outras opções" (pesos exploratory). Em desktop fica em split
 * 2 colunas; em mobile vira abas pra manter a UI utilizável em telas
 * estreitas.
 *
 * UX:
 * - 20 itens visíveis inicial; botão "Ver mais" carrega +20 até o máximo
 *   retornado pela API (até 100 por lado).
 * - Quando `exploratory` está vazio/nulo, cai pra single-list (compat com
 *   resposta antiga ou caso de pool muito pequeno).
 * - Cada coluna tem header próprio explicando a intenção pro curador (sem
 *   jargão técnico — não menciona "engine"/"pesos"/etc).
 */
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Recommendation } from "@/types/recommendation";
import { RecommendationList } from "./RecommendationList";

interface Props {
  primary: Recommendation[];
  exploratory: Recommendation[] | null | undefined;
  vagaId: string;
}

const INITIAL_VISIBLE = 20;
const PAGE_SIZE = 20;

const COLUMN_INFO = {
  primary: {
    title: "Recomendados",
    description:
      "Lista principal, calibrada pra trazer um leque diverso de candidatos, incluindo perfis novos com bom potencial.",
  },
  exploratory: {
    title: "Outras opções",
    description:
      "Lista alternativa, destaca freelancers com histórico forte de aprovações e avaliações.",
  },
};

function ColumnHeader({
  title,
  description,
  count,
}: {
  title: string;
  description: string;
  count: number;
}) {
  return (
    <div className="mb-3 px-1">
      <div className="flex items-baseline justify-between gap-2">
        <h4 className="text-base font-semibold">{title}</h4>
        <span className="text-xs text-muted-foreground shrink-0">
          {count} {count === 1 ? "freelancer" : "freelancers"}
        </span>
      </div>
      <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
    </div>
  );
}

/** Wrapper que renderiza N primeiros e oferece "Ver mais" pra expandir. */
function ListWithPagination({
  recommendations,
  vagaId,
}: {
  recommendations: Recommendation[];
  vagaId: string;
}) {
  const [visible, setVisible] = useState(INITIAL_VISIBLE);
  const total = recommendations.length;
  const slice = recommendations.slice(0, visible);
  const hasMore = visible < total;

  return (
    <div>
      <RecommendationList recommendations={slice} vagaId={vagaId} />
      {hasMore && (
        <div className="mt-4 text-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setVisible((v) => Math.min(v + PAGE_SIZE, total))}
          >
            Ver mais ({total - visible} restantes)
          </Button>
        </div>
      )}
    </div>
  );
}

export function DualRecommendationsList({ primary, exploratory, vagaId }: Props) {
  const hasExploratory = !!exploratory && exploratory.length > 0;

  // Fallback: sem exploratory → single list, igual ao comportamento legado.
  if (!hasExploratory) {
    return <ListWithPagination recommendations={primary} vagaId={vagaId} />;
  }

  return (
    <>
      {/* Desktop (lg+): split horizontal — duas colunas independentes. */}
      <div className="hidden lg:grid lg:grid-cols-2 lg:gap-6">
        <section>
          <ColumnHeader
            title={COLUMN_INFO.primary.title}
            description={COLUMN_INFO.primary.description}
            count={primary.length}
          />
          <ListWithPagination recommendations={primary} vagaId={vagaId} />
        </section>
        <section>
          <ColumnHeader
            title={COLUMN_INFO.exploratory.title}
            description={COLUMN_INFO.exploratory.description}
            count={exploratory!.length}
          />
          <ListWithPagination recommendations={exploratory!} vagaId={vagaId} />
        </section>
      </div>

      {/* Mobile/tablet (< lg): abas — coluna dupla não cabe em tela estreita. */}
      <div className="lg:hidden">
        <Tabs defaultValue="primary" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="primary">
              {COLUMN_INFO.primary.title} ({primary.length})
            </TabsTrigger>
            <TabsTrigger value="exploratory">
              {COLUMN_INFO.exploratory.title} ({exploratory!.length})
            </TabsTrigger>
          </TabsList>
          <TabsContent value="primary" className="mt-3">
            <p className="text-xs text-muted-foreground mb-3 px-1">
              {COLUMN_INFO.primary.description}
            </p>
            <ListWithPagination recommendations={primary} vagaId={vagaId} />
          </TabsContent>
          <TabsContent value="exploratory" className="mt-3">
            <p className="text-xs text-muted-foreground mb-3 px-1">
              {COLUMN_INFO.exploratory.description}
            </p>
            <ListWithPagination recommendations={exploratory!} vagaId={vagaId} />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
